<script setup lang="ts">
/* =============================================================
   BOOK A BERTH
   =============================================================
   §5 of the brief: the last look before the request goes out --
   berth, ship, dates, price and the amenities the ship will use --
   then one button. No payment, as the brief asks.

   What happens next depends on the berth, and the button says so:
   an instant berth is confirmed the moment this is sent; a manual
   one goes to the owner, who confirms or rejects it.

   The amenities offered are the only ones that can be requested
   (the database refuses the rest), so the ship's needs the berth
   lacks are shown as a warning, not as a box that cannot be
   ticked without failing.
   ============================================================= */

import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import PageHeader from '@/components/ui/PageHeader.vue'
import Panel from '@/components/ui/Panel.vue'
import Button from '@/components/ui/Button.vue'
import Alert from '@/components/ui/Alert.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import DetailList from '@/components/ui/DetailList.vue'
import type { DetailRow } from '@/components/ui/DetailList.vue'
import SearchControls from '@/components/search/SearchControls.vue'
import StayQuote from '@/components/search/StayQuote.vue'
import { routes, to } from '@/config/routes'
import { getBerth } from '@/lib/berths'
import { listRates } from '@/lib/rates'
import { listVessels } from '@/lib/vessels'
import { requestBooking } from '@/lib/bookings'
import { searchAroundBerth } from '@/lib/search'
import { useAmenities } from '@/lib/amenities'
import { formatDateTime, formatDuration } from '@/lib/bookingLabels'
import { resultStatus } from '@/lib/searchLabels'
import { errorMessage } from '@/lib/errors'
import { useSearchContext } from '@/lib/useSearchContext'
import { useAuth } from '@/lib/auth/useAuth'
import { useDocumentTitle } from '@/lib/composables/useDocumentTitle'
import type { AmenityCode, Berth, BerthRate, BerthResult, Vessel } from '@/lib/types'

useDocumentTitle('Book a berth')

const route = useRoute()
const router = useRouter()
const { context, amenities, hasStay, update } = useSearchContext()
const { user } = useAuth()
const companyId = () => user.value?.companyId ?? 0
const { amenityLabel } = useAmenities()

const id = computed(() => Number(route.params.id))

const berth = ref<Berth | null>(null)
const rates = ref<BerthRate[]>([])
const vessels = ref<Vessel[]>([])
const result = ref<BerthResult | null>(null)
const requested = ref<AmenityCode[]>([])
const loading = ref(true)
const submitting = ref(false)
const error = ref('')

const vessel = computed(() => vessels.value.find((v) => v.id === context.value.vessel) ?? null)

async function load(): Promise<void> {
  loading.value = true
  error.value = ''
  try {
    const [b, r, v] = await Promise.all([getBerth(id.value), listRates(id.value), listVessels(companyId())])
    berth.value = b
    rates.value = r
    vessels.value = v
    await quote()
  } catch (e) {
    error.value = errorMessage(e, 'Could not load the booking.')
  } finally {
    loading.value = false
  }
}

async function quote(): Promise<void> {
  const b = berth.value
  if (!b) return
  const found = await searchAroundBerth(b, {
    radiusKm: 0.1,
    vessel: vessel.value,
    amenities: amenities.value,
    arrival: hasStay.value ? context.value.from : null,
    departure: hasStay.value ? context.value.to : null,
  })
  result.value = found.result
  // Start from what the ship needs, limited to what this berth offers.
  requested.value = amenities.value.filter((a) => b.amenities.includes(a))
}

onMounted(load)
watch([vessel, () => context.value.from, () => context.value.to, amenities], () => void quote(), { deep: true })

const status = computed(() => (result.value ? resultStatus(result.value, hasStay.value) : null))
const ready = computed(() => Boolean(vessel.value && status.value?.bookable))
const missing = computed(() => result.value?.missing_amenities ?? [])
const isInstant = computed(() => berth.value?.booking_mode === 'instant')

const rows = computed<DetailRow[]>(() => {
  const b = berth.value
  if (!b) return []
  const v = vessel.value
  return [
    { label: 'Berth', value: `${b.name}, ${b.city}`, to: router.resolve(to.berthProfile(b.id, context.value)).fullPath },
    b.company ? { label: 'Owner', value: b.company.name } : { label: 'Owner', empty: true },
    v ? { label: 'Vessel', value: `${v.name} · ${v.length_m} × ${v.width_m} m, ${v.draft_m} m draft` } : { label: 'Vessel', empty: true },
    context.value.from ? { label: 'Arrival', value: formatDateTime(context.value.from) } : { label: 'Arrival', empty: true },
    context.value.to ? { label: 'Departure', value: formatDateTime(context.value.to) } : { label: 'Departure', empty: true },
    hasStay.value && context.value.from && context.value.to
      ? { label: 'Stay', value: formatDuration(context.value.from, context.value.to), mono: true }
      : { label: 'Stay', empty: true },
  ]
})

function toggle(code: AmenityCode): void {
  requested.value = requested.value.includes(code)
    ? requested.value.filter((a) => a !== code)
    : [...requested.value, code]
}

async function submit(): Promise<void> {
  const b = berth.value
  const v = vessel.value
  if (!b || !v || !context.value.from || !context.value.to || submitting.value) return
  submitting.value = true
  error.value = ''
  try {
    const bookingId = await requestBooking({
      berthId: b.id,
      vesselId: v.id,
      arrival: context.value.from,
      departure: context.value.to,
      amenities: requested.value,
    })
    await router.push({ path: to.booking(bookingId), query: { created: '1' } })
  } catch (e) {
    error.value = errorMessage(e, 'Could not place the booking.')
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <p v-if="loading && !berth" class="page-status">Loading…</p>

  <template v-else-if="berth">
    <PageHeader title="Book a berth" :description="`${berth.name}, ${berth.city}`">
      <template #actions>
        <Button variant="ghost" icon="chevronRight" :to="to.berthProfile(berth.id, context)" class="back-link">
          Berth
        </Button>
      </template>
    </PageHeader>

    <Alert v-if="error" tone="danger" class="page-alert">{{ error }}</Alert>

    <div class="layout">
      <div class="main">
        <Panel title="Ship and stay">
          <SearchControls :context="context" :vessels="vessels" @update="update" />
        </Panel>

        <Panel title="Services at the berth" description="What your ship will use. Only what this berth offers can be requested.">
          <div v-if="berth.amenities.length" class="amenities">
            <label v-for="a in berth.amenities" :key="a" class="amenity">
              <input type="checkbox" :checked="requested.includes(a)" @change="toggle(a)" />
              {{ amenityLabel(a) }}
            </label>
          </div>
          <p v-else class="muted">This berth lists no services.</p>
          <Alert v-if="missing.length" tone="warning" class="missing">
            Not available here: {{ missing.map(amenityLabel).join(', ') }}.
          </Alert>
        </Panel>
      </div>

      <aside class="side">
        <Panel title="Summary">
          <DetailList :rows="rows" />
          <div v-if="result" class="quote">
            <StayQuote :result="result" :has-stay="hasStay" :rates="rates" :from="context.from" :to="context.to" />
          </div>

          <p v-if="!vessel" class="needs">Choose the vessel this booking is for.</p>

          <Button full-width icon="check" :loading="submitting" :disabled="!ready" class="submit" @click="submit">
            {{ isInstant ? 'Book now' : 'Send booking request' }}
          </Button>
          <p class="fine">
            {{
              isInstant
                ? 'This berth confirms instantly — the booking is confirmed when you press the button.'
                : 'The owner confirms or rejects this request. You will see the answer under Bookings.'
            }}
            Payment is not part of this demo.
          </p>
        </Panel>
      </aside>
    </div>
  </template>

  <template v-else>
    <Alert v-if="error" tone="danger" class="page-alert">{{ error }}</Alert>
    <EmptyState v-else icon="anchor" title="Berth not found" description="The link may be wrong.">
      <template #action>
        <Button :to="routes.search">Find a berth</Button>
      </template>
    </EmptyState>
  </template>
</template>

<style scoped>
.layout {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(18rem, 1fr);
  gap: var(--space-md);
  align-items: start;
}

.main,
.side {
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
  min-width: 0;
}

.amenities {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-xs) var(--space-md);
}

.amenity {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2xs);
  font-size: var(--text-sm);
  cursor: pointer;
}

.amenity input {
  width: 1rem;
  height: 1rem;
  accent-color: var(--color-accent);
}

.missing {
  margin-top: var(--space-sm);
}

.muted {
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.quote {
  margin-top: var(--space-md);
  padding-top: var(--space-md);
  border-top: var(--border-width) solid var(--color-border);
}

.needs {
  margin-top: var(--space-sm);
  font-size: var(--text-sm);
  color: var(--color-accent-2);
}

.submit {
  margin-top: var(--space-md);
}

.fine {
  margin-top: var(--space-xs);
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

@media (max-width: 62rem) {
  .layout {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
